/**
 * Builds POST /searchFlights body to match production contract:
 * dealType OneWay | RoundTrip, dep_date, origin, destination (IATA-style codes only),
 * room_compositions: [{ adults, kids, infants, kidsAges }].
 * ret_date only for RoundTrip (omitted for OneWay — not sent to backend).
 * No ret_origin / ret_dest; no ISO dates in output.
 * Invoked from fhpService.searchFlights only.
 */

import {
  fromSearchContextLocation,
  toLdsLocationCode,
} from "./flightCanonicalLocation.js";

const HEBREW_CHAR_RE = /[\u0590-\u05FF]/;

/** @param {unknown} value */
function normalizeSearchFlightsDate(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const head = s.split("T")[0];
  const iso = head.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${d}/${m}/${y}`;
  }
  return s;
}

/**
 * Resolve endpoint to a strict uppercase A–Z code (2–4 chars), no Hebrew / city names.
 * @param {unknown} raw
 * @returns {string|undefined}
 */
function endpointToIataLikeCode(raw) {
  const canonical = fromSearchContextLocation(raw);
  if (!canonical) return undefined;
  const code = toLdsLocationCode(canonical);
  if (!code || typeof code !== "string") return undefined;
  const upper = code.trim().toUpperCase();
  if (!/^[A-Z]{2,4}$/.test(upper)) return undefined;
  if (HEBREW_CHAR_RE.test(upper)) return undefined;
  return upper;
}

/**
 * @param {unknown} rooms
 * @returns {{ adults: number, kids: number, infants: number, kidsAges: number[] }[]}
 */
function normalizeRoomCompositions(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const normalized = list.map((room) => {
    if (!room || typeof room !== "object") {
      return { adults: 2, kids: 0, infants: 0, kidsAges: [] };
    }
    const adults =
      Number(room.adults ?? room.AdultsCount ?? 2) || 0;
    let kids = Number(room.kids);
    if (Number.isNaN(kids)) {
      const ages = room.kidsAges ?? room.KidsAges;
      kids = Array.isArray(ages) ? ages.length : 0;
    }
    const infants =
      Number(room.infants ?? room.InfantsCount ?? 0) || 0;
    let kidsAges = room.kidsAges ?? room.KidsAges ?? [];
    if (!Array.isArray(kidsAges)) kidsAges = [];
    const k = Math.max(0, Math.floor(kids));
    const agesCopy = kidsAges.map((a) => Number(a) || 0);
    while (agesCopy.length < k) agesCopy.push(2);
    if (agesCopy.length > k) agesCopy.splice(k);
    return {
      adults: Math.max(0, Math.floor(adults)),
      kids: k,
      infants: Math.max(0, Math.floor(infants)),
      kidsAges: agesCopy,
    };
  });
  if (normalized.length === 0) {
    return [{ adults: 2, kids: 0, infants: 0, kidsAges: [] }];
  }
  return normalized;
}

/**
 * @param {Record<string, unknown>} data
 * @returns {Record<string, unknown>}
 */
export function normalizeSearchFlightsPayloadForLds(data) {
  if (!data || typeof data !== "object") {
    return {
      dealType: "OneWay",
      dep_date: "",
      origin: "",
      destination: "",
      room_compositions: [
        { adults: 2, kids: 0, infants: 0, kidsAges: [] },
      ],
    };
  }

  const dep_date = normalizeSearchFlightsDate(data.dep_date);
  const ret_date_norm = normalizeSearchFlightsDate(data.ret_date);

  if (process.env.NODE_ENV === "development") {
    const logEndpoint = (label, raw) => {
      const canonical = fromSearchContextLocation(raw);
      const tlc = canonical ? toLdsLocationCode(canonical) : undefined;
      const strict = endpointToIataLikeCode(raw);
      console.log(`[searchFlights normalize] ${label} pipeline:`, {
        raw,
        afterFromSearchContextLocation: canonical,
        afterToLdsLocationCode: tlc,
        strictIata2to4: strict === undefined ? "" : strict,
      });
    };
    if ("origin" in data) logEndpoint("origin", data.origin);
    if ("destination" in data) logEndpoint("destination", data.destination);
  }

  const origin = endpointToIataLikeCode(data.origin) ?? "";
  const destination = endpointToIataLikeCode(data.destination) ?? "";
  const room_compositions = normalizeRoomCompositions(
    data.room_compositions
  );

  const explicit =
    data.dealType === "OneWay" || data.dealType === "RoundTrip"
      ? data.dealType
      : null;
  const inferred =
    ret_date_norm && String(ret_date_norm).trim() ? "RoundTrip" : "OneWay";
  const dealType = explicit ?? inferred;

  const out = {
    dealType,
    dep_date,
    origin,
    destination,
    room_compositions,
  };
  if (
    dealType === "RoundTrip" &&
    ret_date_norm &&
    String(ret_date_norm).trim()
  ) {
    out.ret_date = ret_date_norm;
  }
  const agentRaw = data.agent_id ?? data.agentId;
  if (agentRaw != null && String(agentRaw).trim() !== "") {
    out.agent_id = String(agentRaw).trim();
  }
  return out;
}
